import { useNavigate } from 'react-router-dom';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { getChartActivitySecureSSN } from 'api/Patients';

import { failedMsg } from 'lib/constants/messages';

import { showAlert } from 'components/shared/Alert/Alert';

const SecureSSNContext = createContext({
  revealedSSNs: {},
  revealSSN: () => {},
  hideSSN: () => {}
});

export const SecureSSNProvider = SecureSSNContext.Provider;

export const useSecureSSNContext = () => useContext(SecureSSNContext);

export const useSecureSSNState = (entityId) => {
  const navigate = useNavigate();

  const [revealed, setRevealed] = useState({ entityId, ssns: {}, visible: {} });

  if (revealed.entityId !== entityId) setRevealed({ entityId, ssns: {}, visible: {} });

  const { ssns, visible } = revealed;

  const hideSSN = useCallback((historyId) => {
    setRevealed((prev) => {
      const { [historyId]: _hidden, ...visible } = prev.visible;
      const { [historyId]: _forgotten, ...ssns } = prev.ssns;
      return { ...prev, visible, ssns };
    });
  }, []);

  const revealSSN = useCallback(
    async ({ id, history_id }) => {
      if (ssns[history_id]) {
        setRevealed((prev) => ({ ...prev, visible: { ...prev.visible, [history_id]: true } }));
        return;
      }

      try {
        const data = await getChartActivitySecureSSN(navigate, { id, history_id });

        if (!data?.ssn) throw new Error(failedMsg('reveal', 'SSN'));

        setRevealed((prev) =>
          prev.entityId === entityId
            ? {
                ...prev,
                ssns: { ...prev.ssns, [history_id]: data.ssn },
                visible: { ...prev.visible, [history_id]: true }
              }
            : prev
        );
      } catch (error) {
        showAlert({ title: failedMsg('reveal', 'SSN'), color: 'danger' });
      }
    },
    [navigate, entityId, ssns]
  );

  const revealedSSNs = useMemo(
    () =>
      Object.keys(visible).reduce((acc, historyId) => {
        acc[historyId] = ssns[historyId];
        return acc;
      }, {}),
    [ssns, visible]
  );

  return useMemo(() => ({ revealedSSNs, revealSSN, hideSSN }), [revealedSSNs, revealSSN, hideSSN]);
};
